import { STATUS } from "./harvestEntryConstants.js";

/**
 * Pure data reducer — transforms rows array based on action.
 * Does NOT manage undo/redo history (handled by use-undo in useUndoRedo).
 */
export function reducer(rows, action) {
  switch (action.type) {
    case "INITIALIZE":
      return action.payload.rows;

    case "UPDATE_ROWS": {
      const { updatedRows } = action.payload;
      // Key by id ?? _key so synthetic rows (id: null) are matched correctly
      const updatedByKey = new Map(updatedRows.map((r) => [r.id ?? r._key, r]));

      // Apply the basic per-row update
      let nextRows = rows.map((r) => updatedByKey.get(r.id ?? r._key) ?? r);

      // After updating, ensure pass 1 always has the earlier effective date.
      // If pass 1's date > pass 2's date, swap the effective date/yield/status
      // between the two rows so the earlier date stays on pass 1.
      const affectedBlockKeys = new Set(
        updatedRows.map((r) => `${r.paddock}||${r.block}`),
      );

      for (const blockKey of affectedBlockKeys) {
        const [paddock, block] = blockKey.split("||");

        const p1Idx = nextRows.findIndex(
          (r) => r.paddock === paddock && r.block === block && r.pass === 1,
        );
        const p2Idx = nextRows.findIndex(
          (r) => r.paddock === paddock && r.block === block && r.pass === 2,
        );

        if (p1Idx === -1 || p2Idx === -1) continue;

        const p1 = nextRows[p1Idx];
        const p2 = nextRows[p2Idx];

        const effDate1 = p1.pendingHarvestDate ?? p1.harvest_date ?? null;
        const effDate2 = p2.pendingHarvestDate ?? p2.harvest_date ?? null;

        // Only swap when both passes have dates and pass 1 is later than pass 2.
        // YYYY-MM-DD strings compare correctly as ISO dates.
        if (!effDate1 || !effDate2 || effDate1 <= effDate2) continue;

        const effYield1 = p1.pendingYield ?? p1.yield_kg ?? null;
        const effYield2 = p2.pendingYield ?? p2.yield_kg ?? null;
        const effStatus1 = p1.pendingStatus ?? p1.status ?? STATUS.PLANTED;
        const effStatus2 = p2.pendingStatus ?? p2.status ?? STATUS.PLANTED;

        nextRows = [...nextRows];

        // p1 takes p2's effective values (store as pending so save picks them up)
        nextRows[p1Idx] = {
          ...p1,
          pendingHarvestDate: effDate2 === p1.harvest_date ? null : effDate2,
          pendingYield: effYield2 === p1.yield_kg ? null : effYield2,
          pendingStatus: effStatus2 === p1.status ? null : effStatus2,
        };

        // p2 takes p1's effective values
        nextRows[p2Idx] = {
          ...p2,
          pendingHarvestDate: effDate1 === p2.harvest_date ? null : effDate1,
          pendingYield: effYield1 === p2.yield_kg ? null : effYield1,
          pendingStatus: effStatus1 === p2.status ? null : effStatus1,
        };
      }

      return nextRows;
    }

    case "UPDATE_STATUS_BULK": {
      // `keys` is a Set/array of row keys (r.id ?? r._key) from selectedRows
      const { keys, status, defaultDate } = action.payload;
      const keySet = new Set(keys);
      return rows.map((row) => {
        if (!keySet.has(row.id ?? row._key)) return row;
        let next = {
          ...row,
          pendingStatus: status === row.status ? null : status,
        };
        if (
          status === STATUS.HARVESTED &&
          !next.pendingHarvestDate &&
          !next.harvest_date
        ) {
          next.pendingHarvestDate = defaultDate;
        }
        if (status === STATUS.PLANTED) {
          next.pendingYield = null;
          next.pendingHarvestDate = null;
        }
        return next;
      });
    }

    case "SAVE_SUCCESS": {
      // savedRows: [{ _key, id, status, harvest_date, yield_kg }]
      // Moves effective values into saved fields, clears pending, and sets DB id
      // for rows that were previously synthetic (id: null).
      const { savedRows } = action.payload;
      const savedByKey = new Map(savedRows.map((r) => [r._key, r]));
      return rows.map((row) => {
        const saved = savedByKey.get(row._key);
        if (!saved) return row;
        return {
          ...row,
          id: saved.id,
          status: saved.status,
          harvest_date: saved.harvest_date,
          yield_kg: saved.yield_kg,
          pendingStatus: null,
          pendingHarvestDate: null,
          pendingYield: null,
        };
      });
    }

    case "ADD_PASS": {
      const { paddock, block } = action.payload;
      const blockRows = rows.filter(
        (r) => r.paddock === paddock && r.block === block,
      );
      if (blockRows.length === 0) return rows;
      const maxPass = Math.max(...blockRows.map((r) => r.pass));
      if (maxPass >= 10) return rows;
      const newPass = maxPass + 1;
      const refRow = blockRows[0];
      const newRow = {
        id: null,
        _key: `${paddock}||${block}||${newPass}`,
        paddock,
        block,
        pass: newPass,
        area_ha: refRow?.area_ha ?? null,
        average_weight: refRow?.average_weight ?? null,
        status: STATUS.PLANTED,
        harvest_date: null,
        yield_kg: null,
        pendingStatus: null,
        pendingHarvestDate: null,
        pendingYield: null,
      };
      // Insert immediately after the last row for this block
      const lastIdx = rows.reduce(
        (acc, r, i) => (r.paddock === paddock && r.block === block ? i : acc),
        -1,
      );
      const nextRows = [...rows];
      nextRows.splice(lastIdx + 1, 0, newRow);
      return nextRows;
    }

    default:
      return rows;
  }
}
